/**
 * 按钮来源数据增强器
 * 优先级：3（在 NavigationEnricher 和 DOMEnricher 之后）
 * 职责：为 button_click 和 button_expose 事件自动生成 button_source 字段
 *
 * ⚠️ 重要：button_source 生成规则必须与旧版完全一致，因为涉及算法的离线计算
 * 参考：tracker/collecter/buttonClick.js 中的 getTrackerData 函数
 *
 * button_source 格式（与旧版完全一致）：
 * - 有 source: `${page_nm}_${sub_page_nm || ""}_${filter_one[0]}_${source}` 或 `${page_nm}_${sub_page_nm || ""}_${source}`
 * - 无 source: `${page_nm}_${sub_page_nm}_${filter_one[0]}` 或 `${page_nm}_${sub_page_nm}` 或 `${page_nm}`
 */

import { PinDetailContext } from "../context/PinDetailContext";
import { DataEnricher, EnrichContext } from "./types";

export class ButtonSourceEnricher implements DataEnricher {
  name = "ButtonSourceEnricher";
  priority = 3; // 在 NavigationEnricher (1) 和 DOMEnricher (2) 之后

  shouldEnrich(context: EnrichContext): boolean {
    // 在 button_click 和 button_expose 事件时执行
    // event_name 在 TrackerV2.track 中会设置到 trackData 中
    const eventName = context.trackData.event_name || context.trackData.event;
    return eventName === "button_click" || eventName === "button_expose";
  }

  enrich(context: EnrichContext): Record<string, any> {
    const { trackData, targetNode, page } = context;

    // 如果已经存在 button_source，则不覆盖
    if (trackData.button_source) {
      return trackData;
    }

    const { page_nm, sub_page_nm } = page;

    // 获取 source（完全按照旧版逻辑）
    // 旧版逻辑：trackData?.reset_mn ? trackData.module_name : (从 DOM 获取)
    let source: string | undefined;

    if (trackData?.reset_mn && trackData.module_name) {
      // 如果 reset_mn 为 true，使用 module_name
      source = trackData.module_name;
    } else {
      // 否则从 DOM 中获取（优先级：data-content-source > data-module-name > data-dialog-title）
      if (targetNode) {
        source =
          this.getAttribute(targetNode, "data-content-source") ||
          this.getAttribute(targetNode, "data-module-name") ||
          this.getAttribute(targetNode, "data-dialog-title");
      }
    }

    // 单独设置 module_name（新方案：优先从 PinDetailContext 获取，不依赖 DOM）
    // 如果 trackData.reset_mn 为 true 且 trackData.module_name 已存在，则不覆盖
    if (!(trackData?.reset_mn && trackData.module_name)) {
      // 优先从 PinDetailContext 获取（新方案）
      const pinDetailContext = PinDetailContext.get();
      if (pinDetailContext?.content_type) {
        trackData.module_name = pinDetailContext.content_type;
      } else if (targetNode) {
        // 兜底：从 DOM 的 data-module-name 属性获取（兼容旧版）
        const moduleName = this.getAttribute(targetNode, "data-module-name");
        if (moduleName) {
          trackData.module_name = moduleName;
        }
      }
    }

    // 获取 filter（完全按照旧版逻辑，调用 getFilter 函数）
    const filter = this.getFilter(page_nm, targetNode);

    // 生成 button_source（完全按照旧版逻辑）
    if (source) {
      // 有 source 的情况
      if (filter.filter_one && filter.filter_one.length > 0) {
        trackData.button_source = `${page_nm}_${sub_page_nm || ""}_${
          filter.filter_one[0]
        }_${source}`;
      } else {
        trackData.button_source = `${page_nm}_${sub_page_nm || ""}_${source}`;
      }
    } else {
      // 无 source 的情况
      if (filter.filter_one && filter.filter_one.length > 0) {
        trackData.button_source = sub_page_nm
          ? `${page_nm}_${sub_page_nm}_${filter.filter_one[0]}`
          : `${page_nm}_${filter.filter_one[0]}`;
      } else {
        trackData.button_source = sub_page_nm
          ? `${page_nm}_${sub_page_nm}`
          : page_nm;
      }
    }

    // 开发环境日志
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("[ButtonSourceEnricher]:", {
        button_source: trackData.button_source,
        page_nm,
        sub_page_nm,
        source,
        filter,
      });
    }

    return trackData;
  }

  /**
   * 获取 DOM 属性（支持从父元素查找）
   */
  private getAttribute(element: Element, attributeName: string): string | null {
    // 先从当前元素获取
    const value = element.getAttribute(attributeName);
    if (value) {
      return value;
    }

    // 从父元素查找
    let parent = element.parentElement;
    while (parent) {
      const parentValue = parent.getAttribute(attributeName);
      if (parentValue) {
        return parentValue;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  /**
   * 获取 filter（页面筛选器）
   * 完全按照 tracker/collecter/get.js 中的 getFilter 函数实现
   * 返回格式：{ filter_one: string[] | "", filter_two: string[] | "" }
   */
  private getFilter(
    page_nm: string,
    targetNode?: Element | null
  ): { filter_one: string[] | ""; filter_two: string[] | "" } {
    const filter_one: string[] = [];
    const filter_two: string[] = [];

    // 从 DOM 获取 data-filter-one（旧版 getFilter 中，targetNode 默认是 {}，所以这里可能获取不到）
    // 但为了保持一致性，我们还是尝试获取
    if (targetNode) {
      const filterOneAttr = this.getAttribute(targetNode, "data-filter-one");
      if (filterOneAttr) {
        filter_one.push(filterOneAttr);
      }
      const filterTwoAttr = this.getAttribute(targetNode, "data-filter-two");
      if (filterTwoAttr) {
        filter_two.push(filterTwoAttr);
      }
    }

    // 根据页面类型获取 filter（完全按照旧版逻辑）
    if (typeof window === "undefined") {
      return {
        filter_one: filter_one.length ? filter_one : "",
        filter_two: filter_two.length ? filter_two : "",
      };
    }

    try {
      // 发现页或 ai圈页面
      if (page_nm === "发现页" || page_nm === "ai圈页面") {
        const activeFilter = document.getElementById("active-filter-item");
        if (!activeFilter) {
          return {
            filter_one: filter_one.length ? filter_one : "",
            filter_two: filter_two.length ? filter_two : "",
          };
        }
        filter_one.push(activeFilter.innerText);

        // 在发现页画板页面，需要获取画板ID
        try {
          // 尝试从 Next.js Router 获取
          const router = (window as any).__NEXT_ROUTER__;
          if (router?.query?.board) {
            filter_two.push(router.query.board);
          }
        } catch (e) {
          // 静默失败
        }
      }

      // 热点日历聚合页
      if (page_nm === "热点日历聚合页") {
        const filterOne =
          document.getElementById("active-filter-one")?.innerText;
        const filterTwo =
          document.getElementById("active-filter-two")?.innerText;
        if (filterOne) {
          filter_one.push(filterOne);
        }
        if (filterTwo) {
          filter_two.push(filterTwo);
        }
      }

      // 搜索结果页
      if (page_nm === "搜索结果页") {
        try {
          // 尝试从 Next.js Router 获取
          const router = (window as any).__NEXT_ROUTER__;
          if (router?.query) {
            const { type, category } = router.query;

            switch (type) {
              case "board":
                filter_one.push("画板");
                break;
              case "user":
                filter_one.push("用户");
                break;
              case "pin":
                filter_one.push("采集");
                // 二级筛选项_分类信息
                try {
                  // 尝试从 useAppStore 获取 categories
                  const useAppStore = require("@/context/store").default;
                  const categories = useAppStore.getState()?.categories || [];
                  const categoryName =
                    categories.find((item: any) => item?.id === category)
                      ?.name || "全部";
                  filter_two.push(categoryName);
                } catch (e) {
                  filter_two.push("全部");
                }
                break;
              case "material":
                filter_one.push("素材");
                break;
              case "aigc":
                filter_one.push("aigc");
                break;
              default:
                // 默认采集搜索情况
                const activeTab = document.querySelector(
                  ".ant-tabs-tab-active"
                );
                if (activeTab?.children[0]) {
                  filter_one.push(
                    (activeTab.children[0] as HTMLElement).innerText
                  );
                  // 二级筛选项_分类信息
                  try {
                    const useAppStore = require("@/context/store").default;
                    const categories = useAppStore.getState()?.categories || [];
                    const categoryName =
                      categories.find((item: any) => item?.id === category)
                        ?.name || "全部";
                    filter_two.push(categoryName);
                  } catch (e) {
                    filter_two.push("全部");
                  }
                }
                break;
            }
          }
        } catch (error) {
          // 静默失败，不影响主流程
          if (process.env.NODE_ENV === "development") {
            console.warn(
              "[ButtonSourceEnricher] Error getting filter for 搜索结果页:",
              error
            );
          }
        }
      }

      // 花瓣素材首页
      if (page_nm === "花瓣素材首页") {
        return {
          filter_one: "",
          filter_two: "",
        };
      }
    } catch (error) {
      // 静默失败，不影响主流程
      if (process.env.NODE_ENV === "development") {
        console.warn("[ButtonSourceEnricher] Error getting filter:", error);
      }
    }

    // 返回格式与旧版完全一致
    return {
      filter_one: filter_one.length ? filter_one : "",
      filter_two: filter_two.length ? filter_two : "",
    };
  }
}
